import React, { useState } from "react";
import {
	Container,
	Grid,
	Paper,
	Button,
	TextField,
	Typography,
	IconButton,
	Tabs,
	Tab,
} from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import axios from "axios";
import { withSnackbar } from "notistack";
import CardCollectionSimulatorView from "./CardCollectionSimulatorView";

const useStyles = (theme) => ({
	container: {
		paddingTop: theme.spacing(4),
		paddingBottom: theme.spacing(4),
	},
	paper: {
		padding: theme.spacing(2),
		display: "flex",
		overflow: "auto",
		flexDirection: "column",
		marginBottom: theme.spacing(2),
	},
	formRow: {
		display: "flex",
		alignItems: "center",
		marginBottom: theme.spacing(1),
		gap: theme.spacing(2),
	},
	button: {
		marginTop: theme.spacing(1),
		marginRight: theme.spacing(1),
	},
	jsonViewer: {
		backgroundColor: "#f5f5f5",
		padding: theme.spacing(2),
		borderRadius: 4,
		maxHeight: 400,
		overflow: "auto",
		whiteSpace: "pre-wrap",
		wordBreak: "break-all",
		fontFamily: "monospace",
		fontSize: 12,
	},
	sectionTitle: {
		marginBottom: theme.spacing(1),
		fontWeight: "bold",
	},
	textField: {
		marginRight: theme.spacing(2),
		width: 200,
	},
});

// Pack type constants matching ccenums.CardCollectionPackType
const PACK_TYPES = [1, 2, 3, 4, 5];

// Valid room costs (bet amounts) from config.RoomCosts across all room types
const ROOM_COSTS = [
	500, 2500, 5000, 10000, 20000, 50000, 250000, 1000000, 5000000, 10000000,
	20000000,
];

const CardCollectionView = ({ classes, dev, enqueueSnackbar }) => {
	const [userId, setUserId] = useState("");
	const [cardCollectionData, setCardCollectionData] = useState(null);
	const [loading, setLoading] = useState(false);

	// Tab toggle: 0 = data tools, 1 = simulator (both scoped to card collection).
	const [tab, setTab] = useState(0);

	// Add Card Pack: packType (int 1-5) + cards [{setId, cardNumber}]
	const [packType, setPackType] = useState("1");
	const [packCards, setPackCards] = useState([{ setId: "", cardNumber: "" }]);

	// Add Cards to SetData: [{setId, cardNumber}]
	const [addCards, setAddCards] = useState([{ setId: "", cardNumber: "" }]);

	// Wildcard: unclaimedWildCardsCount + duplicateStarPoints
	const [unclaimedWildCardsCount, setUnclaimedWildCardsCount] = useState("");
	const [duplicateStarPoints, setDuplicateStarPoints] = useState("");

	// Dispense Win Pack: packType + roomCost
	const [winPackType, setWinPackType] = useState("1");
	const [roomCost, setRoomCost] = useState("500");

	const fetchCardCollection = () => {
		if (!userId) {
			enqueueSnackbar("User ID is required", { variant: "error" });
			return;
		}
		setLoading(true);
		axios
			.get("/api/dev/" + dev + "/cardcollection/" + userId)
			.then((res) => res.data)
			.then((data) => {
				setLoading(false);
				if (data && data.err) {
					enqueueSnackbar(data.err, { variant: "error" });
					setCardCollectionData(null);
				} else {
					setCardCollectionData(data);
					enqueueSnackbar("Card collection fetched", { variant: "success" });
				}
			})
			.catch((err) => {
				setLoading(false);
				enqueueSnackbar("Error fetching card collection: " + err, {
					variant: "error",
				});
			});
	};

	const resetCardCollection = () => {
		if (!userId) {
			enqueueSnackbar("User ID is required", { variant: "error" });
			return;
		}
		setLoading(true);
		axios
			.delete("/api/dev/" + dev + "/cardcollection/" + userId)
			.then((res) => res.data)
			.then((data) => {
				setLoading(false);
				if (data && data.err) {
					enqueueSnackbar(data.err, { variant: "error" });
				} else {
					setCardCollectionData(null);
					enqueueSnackbar("Card collection reset", { variant: "success" });
				}
			})
			.catch((err) => {
				setLoading(false);
				enqueueSnackbar("Error resetting card collection: " + err, {
					variant: "error",
				});
			});
	};

	// Fill Set (add all cards)
	const [fillSetId, setFillSetId] = useState("");

	const submitFillSet = () => {
		if (!userId) {
			enqueueSnackbar("User ID is required", { variant: "error" });
			return;
		}
		if (!fillSetId) {
			enqueueSnackbar("Set ID is required", { variant: "error" });
			return;
		}
		setLoading(true);
		axios
			.post("/api/dev/" + dev + "/cardcollection/" + userId + "/fillset", { setId: fillSetId })
			.then((res) => res.data)
			.then((data) => {
				setLoading(false);
				if (data && data.err) {
					enqueueSnackbar(data.err, { variant: "error" });
				} else {
					enqueueSnackbar("All cards added to set", { variant: "success" });
					setFillSetId("");
				}
			})
			.catch((err) => {
				setLoading(false);
				enqueueSnackbar("Error filling set: " + err, { variant: "error" });
			});
	};

	// Remove Cards from Set
	const [removeSetId, setRemoveSetId] = useState("");
	const [removeCardNumbers, setRemoveCardNumbers] = useState("");

	const submitRemoveCardsFromSet = () => {
		if (!userId) {
			enqueueSnackbar("User ID is required", { variant: "error" });
			return;
		}
		if (!removeSetId) {
			enqueueSnackbar("Set ID is required", { variant: "error" });
			return;
		}
		const cardNumbers = removeCardNumbers
			.split(",")
			.map((s) => parseInt(s.trim(), 10))
			.filter((n) => !isNaN(n));
		if (cardNumbers.length === 0) {
			enqueueSnackbar("At least one card number is required", { variant: "error" });
			return;
		}
		setLoading(true);
		axios
			.post(
				"/api/dev/" + dev + "/cardcollection/" + userId + "/removecards",
				{ setId: removeSetId, cardNumbers },
			)
			.then((res) => res.data)
			.then((data) => {
				setLoading(false);
				if (data && data.err) {
					enqueueSnackbar(data.err, { variant: "error" });
				} else {
					enqueueSnackbar("Cards removed from set", { variant: "success" });
					setRemoveSetId("");
					setRemoveCardNumbers("");
				}
			})
			.catch((err) => {
				setLoading(false);
				enqueueSnackbar("Error removing cards: " + err, { variant: "error" });
			});
	};

	const clearUnopenedCardPacks = () => {
		if (!userId) {
			enqueueSnackbar("User ID is required", { variant: "error" });
			return;
		}
		setLoading(true);
		axios
			.delete("/api/dev/" + dev + "/cardcollection/" + userId + "/unopenedpacks")
			.then((res) => res.data)
			.then((data) => {
				setLoading(false);
				if (data && data.err) {
					enqueueSnackbar(data.err, { variant: "error" });
				} else {
					enqueueSnackbar("Unopened card packs cleared", { variant: "success" });
				}
			})
			.catch((err) => {
				setLoading(false);
				enqueueSnackbar("Error clearing unopened packs: " + err, {
					variant: "error",
				});
			});
	};

	const submitAddCardPack = () => {
		if (!userId) {
			enqueueSnackbar("User ID is required", { variant: "error" });
			return;
		}
		const cards = packCards
			.filter((c) => c.setId !== "" && c.cardNumber !== "")
			.map((c) => ({ setId: c.setId, cardNumber: parseInt(c.cardNumber, 10) }));

		if (cards.length === 0) {
			enqueueSnackbar("At least one card is required", { variant: "error" });
			return;
		}

		const payload = { packType: parseInt(packType, 10), cards };
		setLoading(true);
		axios
			.post(
				"/api/dev/" + dev + "/cardcollection/" + userId + "/addpack",
				payload,
			)
			.then((res) => res.data)
			.then((data) => {
				setLoading(false);
				if (data && data.err) {
					enqueueSnackbar(data.err, { variant: "error" });
				} else {
					enqueueSnackbar("Card pack added", { variant: "success" });
					setPackCards([{ setId: "", cardNumber: "" }]);
				}
			})
			.catch((err) => {
				setLoading(false);
				enqueueSnackbar("Error adding card pack: " + err, { variant: "error" });
			});
	};

	const submitAddCards = () => {
		if (!userId) {
			enqueueSnackbar("User ID is required", { variant: "error" });
			return;
		}
		const payload = addCards
			.filter((c) => c.setId !== "" && c.cardNumber !== "")
			.map((c) => ({ setId: c.setId, cardNumber: parseInt(c.cardNumber, 10) }));

		if (payload.length === 0) {
			enqueueSnackbar("At least one card entry is required", {
				variant: "error",
			});
			return;
		}
		setLoading(true);
		axios
			.post(
				"/api/dev/" + dev + "/cardcollection/" + userId + "/addcards",
				payload,
			)
			.then((res) => res.data)
			.then((data) => {
				setLoading(false);
				if (data && data.err) {
					enqueueSnackbar(data.err, { variant: "error" });
				} else {
					enqueueSnackbar("Cards added to album", { variant: "success" });
					setAddCards([{ setId: "", cardNumber: "" }]);
				}
			})
			.catch((err) => {
				setLoading(false);
				enqueueSnackbar("Error adding cards: " + err, { variant: "error" });
			});
	};

	const submitWildcard = () => {
		if (!userId) {
			enqueueSnackbar("User ID is required", { variant: "error" });
			return;
		}
		const payload = {
			unclaimedWildCardsCount: parseInt(unclaimedWildCardsCount, 10) || 0,
			duplicateStarPoints: parseInt(duplicateStarPoints, 10) || 0,
		};
		setLoading(true);
		axios
			.post(
				"/api/dev/" + dev + "/cardcollection/" + userId + "/wildcard",
				payload,
			)
			.then((res) => res.data)
			.then((data) => {
				setLoading(false);
				if (data && data.err) {
					enqueueSnackbar(data.err, { variant: "error" });
				} else {
					enqueueSnackbar("Wildcard data set", { variant: "success" });
					setUnclaimedWildCardsCount("");
					setDuplicateStarPoints("");
				}
			})
			.catch((err) => {
				setLoading(false);
				enqueueSnackbar("Error setting wildcard data: " + err, {
					variant: "error",
				});
			});
	};

	const submitDispenseWinPack = () => {
		if (!userId) {
			enqueueSnackbar("User ID is required", { variant: "error" });
			return;
		}
		const payload = {
			packType: parseInt(winPackType, 10),
			roomCost: parseInt(roomCost, 10) || 0,
		};
		setLoading(true);
		axios
			.post(
				"/api/dev/" + dev + "/cardcollection/" + userId + "/dispensewinpack",
				payload,
			)
			.then((res) => res.data)
			.then((data) => {
				setLoading(false);
				if (data && data.err) {
					enqueueSnackbar(data.err, { variant: "error" });
				} else {
					enqueueSnackbar("Dispense win pack called (fetch to verify)", {
						variant: "success",
					});
				}
			})
			.catch((err) => {
				setLoading(false);
				enqueueSnackbar("Error dispensing win pack: " + err, {
					variant: "error",
				});
			});
	};

	// Pack cards helpers
	const handlePackCardChange = (index, field, value) => {
		const updated = [...packCards];
		updated[index] = { ...updated[index], [field]: value };
		setPackCards(updated);
	};
	const addPackCardRow = () =>
		setPackCards([...packCards, { setId: "", cardNumber: "" }]);
	const removePackCardRow = (index) => {
		const updated = packCards.filter((_, i) => i !== index);
		setPackCards(
			updated.length > 0 ? updated : [{ setId: "", cardNumber: "" }],
		);
	};

	// Add cards helpers
	const handleAddCardChange = (index, field, value) => {
		const updated = [...addCards];
		updated[index] = { ...updated[index], [field]: value };
		setAddCards(updated);
	};
	const addCardRow = () =>
		setAddCards([...addCards, { setId: "", cardNumber: "" }]);
	const removeCardRow = (index) => {
		const updated = addCards.filter((_, i) => i !== index);
		setAddCards(updated.length > 0 ? updated : [{ setId: "", cardNumber: "" }]);
	};

	return (
		<Container maxWidth="lg" className={classes.container}>
			<Tabs
				value={tab}
				onChange={(e, v) => setTab(v)}
				indicatorColor="primary"
				textColor="primary"
				style={{ marginBottom: 16 }}
			>
				<Tab label="Data Tools" />
				<Tab label="Simulator" />
			</Tabs>
			{tab === 1 && <CardCollectionSimulatorView dev={dev} />}
			{tab === 0 && (
			<Grid container spacing={3}>
				<Grid item xs={12}>
					{/* Fetch / Reset */}
					<Paper className={classes.paper}>
						<Typography variant="h6" className={classes.sectionTitle}>
							Card Collection Viewer
						</Typography>
						<div className={classes.formRow}>
							<TextField
								className={classes.textField}
								label="User ID (gameCode)"
								value={userId}
								onChange={(e) => setUserId(e.target.value)}
								variant="outlined"
								size="small"
							/>
							<Button
								className={classes.button}
								variant="contained"
								color="primary"
								onClick={fetchCardCollection}
								disabled={loading || !userId}
							>
								{loading ? "Loading..." : "Fetch"}
							</Button>
							<Button
								className={classes.button}
								variant="contained"
								color="secondary"
								onClick={resetCardCollection}
								disabled={loading || !userId}
							>
								Reset
							</Button>
						</div>
						{cardCollectionData && (
							<pre className={classes.jsonViewer}>
								{JSON.stringify(cardCollectionData, null, 2)}
							</pre>
						)}
					</Paper>

					{/* Add Unopened Card Pack */}
					<Paper className={classes.paper}>
						<Typography variant="h6" className={classes.sectionTitle}>
							Add Unopened Card Pack
						</Typography>
						<div className={classes.formRow}>
							<TextField
								className={classes.textField}
								label="Pack Type (1–5)"
								select
								SelectProps={{ native: true }}
								value={packType}
								onChange={(e) => setPackType(e.target.value)}
								variant="outlined"
								size="small"
							>
								{PACK_TYPES.map((pt) => (
									<option key={pt} value={pt}>
										PACK_{pt}
									</option>
								))}
							</TextField>
						</div>
						<Typography variant="subtitle2">Cards in pack:</Typography>
						{packCards.map((card, index) => (
							<div key={index} className={classes.formRow}>
								<TextField
									className={classes.textField}
									label="Set ID"
									value={card.setId}
									onChange={(e) =>
										handlePackCardChange(index, "setId", e.target.value)
									}
									variant="outlined"
									size="small"
								/>
								<TextField
									className={classes.textField}
									label="Card Number (1–9)"
									type="number"
									value={card.cardNumber}
									onChange={(e) =>
										handlePackCardChange(index, "cardNumber", e.target.value)
									}
									variant="outlined"
									size="small"
								/>
								<IconButton
									size="small"
									onClick={() => removePackCardRow(index)}
								>
									<DeleteIcon fontSize="small" />
								</IconButton>
							</div>
						))}
						<div>
							<IconButton size="small" onClick={addPackCardRow}>
								<AddIcon fontSize="small" />
							</IconButton>
						</div>
						<Button
							className={classes.button}
							variant="contained"
							color="primary"
							onClick={submitAddCardPack}
							disabled={loading || !userId}
						>
							Add Pack
						</Button>
						<Button
							className={classes.button}
							variant="contained"
							color="secondary"
							onClick={clearUnopenedCardPacks}
							disabled={loading || !userId}
						>
							Clear Unopened Packs
						</Button>
					</Paper>

					{/* Add Cards to SetData */}
					<Paper className={classes.paper}>
						<Typography variant="h6" className={classes.sectionTitle}>
							Add Cards to Album (SetData)
						</Typography>
						{addCards.map((card, index) => (
							<div key={index} className={classes.formRow}>
								<TextField
									className={classes.textField}
									label="Set ID"
									value={card.setId}
									onChange={(e) =>
										handleAddCardChange(index, "setId", e.target.value)
									}
									variant="outlined"
									size="small"
								/>
								<TextField
									className={classes.textField}
									label="Card Number (1–9)"
									type="number"
									value={card.cardNumber}
									onChange={(e) =>
										handleAddCardChange(index, "cardNumber", e.target.value)
									}
									variant="outlined"
									size="small"
								/>
								<IconButton size="small" onClick={() => removeCardRow(index)}>
									<DeleteIcon fontSize="small" />
								</IconButton>
							</div>
						))}
						<div>
							<IconButton size="small" onClick={addCardRow}>
								<AddIcon fontSize="small" />
							</IconButton>
						</div>
						<Button
							className={classes.button}
							variant="contained"
							color="primary"
							onClick={submitAddCards}
							disabled={loading || !userId}
						>
							Add Cards
						</Button>
					</Paper>

					{/* Fill Set (Add All Cards) */}
					<Paper className={classes.paper}>
						<Typography variant="h6" className={classes.sectionTitle}>
							Fill Set (Add Cards 1–8)
						</Typography>
						<div className={classes.formRow}>
							<TextField
								className={classes.textField}
								label="Set ID"
								value={fillSetId}
								onChange={(e) => setFillSetId(e.target.value)}
								variant="outlined"
								size="small"
							/>
						</div>
						<Button
							className={classes.button}
							variant="contained"
							color="primary"
							onClick={submitFillSet}
							disabled={loading || !userId}
						>
							Fill Set
						</Button>
					</Paper>

					{/* Set Wildcard Data */}
					<Paper className={classes.paper}>
						<Typography variant="h6" className={classes.sectionTitle}>
							Set Wildcard Data
						</Typography>
						<div className={classes.formRow}>
							<TextField
								className={classes.textField}
								label="Unclaimed Wild Cards"
								type="number"
								value={unclaimedWildCardsCount}
								onChange={(e) => setUnclaimedWildCardsCount(e.target.value)}
								variant="outlined"
								size="small"
							/>
							<TextField
								className={classes.textField}
								label="Duplicate Star Points"
								type="number"
								value={duplicateStarPoints}
								onChange={(e) => setDuplicateStarPoints(e.target.value)}
								variant="outlined"
								size="small"
							/>
						</div>
						<Button
							className={classes.button}
							variant="contained"
							color="primary"
							onClick={submitWildcard}
							disabled={loading || !userId}
						>
							Set Wildcard
						</Button>
					</Paper>
					{/* Remove Cards from Set */}
					<Paper className={classes.paper}>
						<Typography variant="h6" className={classes.sectionTitle}>
							Remove Cards from Set
						</Typography>
						<div className={classes.formRow}>
							<TextField
								className={classes.textField}
								label="Set ID"
								value={removeSetId}
								onChange={(e) => setRemoveSetId(e.target.value)}
								variant="outlined"
								size="small"
							/>
							<TextField
								className={classes.textField}
								label="Card Numbers (comma-separated)"
								value={removeCardNumbers}
								onChange={(e) => setRemoveCardNumbers(e.target.value)}
								variant="outlined"
								size="small"
								placeholder="1,3,5"
							/>
						</div>
						<Button
							className={classes.button}
							variant="contained"
							color="secondary"
							onClick={submitRemoveCardsFromSet}
							disabled={loading || !userId}
						>
							Remove Cards
						</Button>
					</Paper>

					{/* Dispense Win Pack */}
					<Paper className={classes.paper}>
						<Typography variant="h6" className={classes.sectionTitle}>
							Dispense Win Pack
						</Typography>
						<div className={classes.formRow}>
							<TextField
								className={classes.textField}
								label="Pack Type (1–5)"
								select
								SelectProps={{ native: true }}
								value={winPackType}
								onChange={(e) => setWinPackType(e.target.value)}
								variant="outlined"
								size="small"
							>
								{PACK_TYPES.map((pt) => (
									<option key={pt} value={pt}>
										PACK_{pt}
									</option>
								))}
							</TextField>
							<TextField
								className={classes.textField}
								label="Room Cost"
								select
								SelectProps={{ native: true }}
								value={roomCost}
								onChange={(e) => setRoomCost(e.target.value)}
								variant="outlined"
								size="small"
							>
								{ROOM_COSTS.map((cost) => (
									<option key={cost} value={cost}>
										{cost.toLocaleString()}
									</option>
								))}
							</TextField>
						</div>
						<Button
							className={classes.button}
							variant="contained"
							color="primary"
							onClick={submitDispenseWinPack}
							disabled={loading || !userId}
						>
							Dispense Win Pack
						</Button>
					</Paper>
				</Grid>
			</Grid>
			)}
		</Container>
	);
};

export default withStyles(useStyles)(withSnackbar(CardCollectionView));
